import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {IconBrain} from '@tabler/icons-react';
import {memo} from 'react';
import type {FC} from 'react';
import type {
  AssistantChartResponse,
  AssistantMemoryResponse,
  AssistantMessageFeedbackResponse,
  AssistantTableResponse,
  AssistantToolCallResponse,
  AssistantUsedMemoryResponse,
} from 'src/api/ProactApiClient';
import {AssistantMarkdown} from 'src/app/assistant/AssistantMarkdown';
import {
  isDatasetTool,
  showsToolRow,
} from 'src/app/assistant/assistantToolLabels';
import {AssistantMemoryCard} from 'src/app/assistant/Memory/AssistantMemoryCard';
import {ChatChart} from 'src/app/assistant/Chart/ChatChart';
import {ChatTable} from 'src/app/assistant/Table/ChatTable';
import {SidebarLinkAssistant} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {CopyActionIcon} from 'src/design/molecules/CopyActionIcon';
import {stripMemoryMarkers} from 'src/shared/assistant/memoryMarkers';
import classes from './Assistant.module.css';
import {MessageFeedbackActions} from './MessageFeedbackActions';
import {ToolCallRow} from './ToolCallRow';

export interface ChatMessageProps {
  role: string;
  content: string;
  truncated?: boolean;
  streaming?: boolean;
  toolCalls?: AssistantToolCallResponse[];
  charts?: AssistantChartResponse[];
  tables?: AssistantTableResponse[];
  /** 1-based number of this row's first query within its reply. */
  queryOrdinalStart?: number;
  conversationId?: number;
  messageId?: number;
  /** A persisted final reply that shows the thumbs vote. */
  rateable?: boolean;
  feedback?: AssistantMessageFeedbackResponse | null;
  /** Notes a memory tool saved on this row. */
  memories?: AssistantMemoryResponse[];
  /** Notes the reply reported using through its markers. */
  usedMemories?: AssistantUsedMemoryResponse[];
  thinkHarder?: boolean;
  onOpenMemory?: () => void;
}

const isAssistant = (role: string) => role.toLowerCase() === 'assistant';

const EMPTY_CHARTS: AssistantChartResponse[] = [];
const EMPTY_TABLES: AssistantTableResponse[] = [];
const EMPTY_MEMORIES: AssistantMemoryResponse[] = [];
const EMPTY_USED_MEMORIES: AssistantUsedMemoryResponse[] = [];
const NOOP = () => {};

// Memoized: ChatTranscript re-renders on every SSE text delta, but stored
// message props are stable, so this avoids re-parsing markdown per delta.
export const ChatMessage: FC<ChatMessageProps> = memo(function ChatMessage({
  role,
  content,
  truncated,
  streaming,
  toolCalls,
  charts = EMPTY_CHARTS,
  tables = EMPTY_TABLES,
  queryOrdinalStart = 1,
  conversationId,
  messageId,
  rateable = false,
  feedback,
  memories = EMPTY_MEMORIES,
  usedMemories = EMPTY_USED_MEMORIES,
  thinkHarder = false,
  onOpenMemory = NOOP,
}) {
  if (!isAssistant(role)) {
    return (
      <Group justify="flex-end" align="flex-start">
        <Box
          bg="var(--mantine-color-gray-1)"
          style={{
            borderRadius: 'var(--mantine-radius-lg)',
            padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
            maxWidth: '75%',
          }}>
          <Text style={{whiteSpace: 'pre-wrap'}} c="dark">
            {content}
          </Text>
        </Box>
      </Group>
    );
  }

  const displayContent = stripMemoryMarkers(content, {streaming});
  const hasContent = displayContent.trim().length > 0;
  const canExport = !streaming && conversationId !== undefined;
  // A chart, table, or memory card replaces its tool row; a failed render leaves no trace.
  const shownCalls = (toolCalls ?? []).filter(call => showsToolRow(call.name));
  let queryOrdinal = queryOrdinalStart;
  const toolRows = shownCalls.map((call, index) => {
    const isQuery = isDatasetTool(call.name);
    const ordinal = isQuery ? queryOrdinal++ : 0;
    return (
      <ToolCallRow
        key={index}
        tool={call.name}
        download={
          isQuery && canExport && call.id
            ? {conversationId, toolUseId: call.id, ordinal}
            : undefined
        }
      />
    );
  });
  const chartViews = charts.map((chart, index) => (
    <ChatChart key={index} chart={chart} />
  ));
  const tableViews = tables.map((table, index) => (
    <ChatTable
      key={index}
      table={table}
      conversationId={conversationId}
      canExport={canExport}
    />
  ));

  const memoryCards = memories.map(note => (
    <AssistantMemoryCard key={note.id} note={note} onManage={onOpenMemory} />
  ));

  // Empty response that only carried tool calls renders as a compact row set,
  // not an empty assistant bubble with a header and copy button.
  if (!hasContent && (toolCalls?.length ?? 0) > 0) {
    if (
      toolRows.length === 0 &&
      chartViews.length === 0 &&
      tableViews.length === 0 &&
      memoryCards.length === 0
    )
      return null;
    return (
      <Box className={classes.assistantMsg}>
        <Stack gap="xs">
          {toolRows}
          {chartViews}
          {tableViews}
          {memoryCards}
        </Stack>
      </Box>
    );
  }

  const showCopy = !streaming && hasContent;
  const isFinalRow = !streaming && (toolCalls?.length ?? 0) === 0;
  const showUsedMemory = isFinalRow && usedMemories.length > 0;
  const showThoughtHarder = isFinalRow && thinkHarder;
  const showFeedback =
    rateable &&
    !streaming &&
    conversationId !== undefined &&
    messageId !== undefined;

  return (
    <Box className={classes.assistantMsg}>
      <Stack gap="xs">
        <Group gap={6} mb={4} align="center">
          <ThemeIcon size="xs" variant="light" color="gray">
            {SidebarLinkAssistant.icon}
          </ThemeIcon>
          <Text
            size="xs"
            fw={600}
            c="dimmed"
            tt="uppercase"
            style={{letterSpacing: '0.4px'}}>
            Assistant
          </Text>
        </Group>

        <AssistantMarkdown content={displayContent} />

        {toolRows}
        {chartViews}
        {tableViews}
        {memoryCards}

        {truncated && (
          <Text size="xs" c="dimmed" fs="italic">
            Response was truncated.
          </Text>
        )}

        {(showCopy || showFeedback || showUsedMemory || showThoughtHarder) && (
          <Group gap={2} wrap="nowrap" align="center">
            {showCopy && <CopyActionIcon value={displayContent} iconSize={16} />}
            {showFeedback && (
              <MessageFeedbackActions
                conversationId={conversationId}
                messageId={messageId}
                feedback={feedback}
              />
            )}
            {showUsedMemory && (
              <Tooltip
                multiline
                maw={320}
                label={usedMemories.map(note => note.text).join(' · ')}>
                <UnstyledButton
                  ml="xs"
                  aria-label="Used memory - manage memory"
                  onClick={onOpenMemory}>
                  <Group gap={4} wrap="nowrap" c="dimmed">
                    <IconBrain size={12} aria-hidden />
                    <Text size="xs" c="dimmed">
                      Used memory
                    </Text>
                  </Group>
                </UnstyledButton>
              </Tooltip>
            )}
            {showThoughtHarder && (
              <Text size="xs" c="dimmed" ml="xs">
                Thought harder
              </Text>
            )}
          </Group>
        )}
      </Stack>
    </Box>
  );
});
